import React, {useLayoutEffect, useState} from "react";
import {ButtonBase, Typography} from "@material-ui/core";
import BackIcon from "@material-ui/icons/KeyboardBackspace";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useTranslate} from "@tolgee/react";
import {selectUser} from "store/auth/selectors";
import {apiV2, signInWithCustomToken} from "api/firebase";
import {cancelSessionSwitch} from "tools/auth/sessionSync";
import {prepareSessionSwitch} from "tools/auth/logoutCleanup";
import {APP_PATH} from "view/routes";
import useElementSizeListener from "hooks/ui/useElementSizeListener";
import {useImpersonationBannerStore} from "store/general/impersonationBanner.store";
import useStyles from "./styles";

type Props = {
  fullName: string;
};

const ImpersonationBanner: React.FC<Props> = ({fullName}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const {t} = useTranslate();
  const {
    impersonatedByEmail,
    impersonatedByRole,
    returnTokenExpiresAt,
  } = useSelector(selectUser);
  const [rootRef, size] = useElementSizeListener<HTMLDivElement>();
  const updateBannerHeight = useImpersonationBannerStore(
    (state) => state.updateBannerHeight
  );
  const [isReturning, setIsReturning] = useState(false);

  const isReturnAvailable = Boolean(
    returnTokenExpiresAt &&
      Date.now() < new Date(returnTokenExpiresAt).getTime()
  );

  useLayoutEffect(() => {
    updateBannerHeight(impersonatedByEmail ? size.height : 0);
  }, [impersonatedByEmail, size.height, updateBannerHeight]);

  if (!impersonatedByEmail) {
    return null;
  }

  const handleReturn = async () => {
    if (isReturning) return;
    setIsReturning(true);
    try {
      const {customToken} = await apiV2.auth.impersonateReturn();
      // Tear down the impersonated (exhibitor) client state — zustand stores
      // and their notification/idle subscriptions, react-query cache, Firebase
      // session — before signing in as the returning user, so no stale
      // show-scoped request refires under the organiser session and 403s.
      // Runs AFTER `impersonateReturn`, which needs the still-live impersonated
      // Firebase token + session cookie to mint the returning custom token.
      await prepareSessionSwitch();
      // Land on the role-agnostic home BEFORE the returning identity resolves,
      // so the shared Layout no longer holds the impersonated show id when the
      // role flips. Otherwise its show-scoped effects re-fetch under the wrong
      // role during the swap and 403.
      navigate(APP_PATH);
      await signInWithCustomToken(customToken);
    } catch (error) {
      console.error("Failed to return from impersonation", error);
      cancelSessionSwitch();
      setIsReturning(false);
    }
  };

  return (
    <div ref={rootRef} className={classes.root}>
      {isReturnAvailable && (
        <ButtonBase className={classes.returnButton} onClick={handleReturn}>
          <BackIcon className={classes.backIcon} />
          {t("common.impersonation.return_to_account", {
            role: impersonatedByRole,
          })}
        </ButtonBase>
      )}
      <Typography variant="inherit">
        {impersonatedByRole
          ? t("common.impersonation.viewing_as_role", {
              name: fullName,
              role: impersonatedByRole,
            })
          : t("common.impersonation.viewing_as", {name: fullName})}
      </Typography>
    </div>
  );
};

export default ImpersonationBanner;
